import Link from 'next/link'

import classNames from 'classnames'
import { Spacer } from 'ethos-design-system'
import {
  ANALYTICS_SERVICE_KEYS,
  ANALYTICS_SERVICE_LABELS,
  MODULES,
} from 'lib/@getethos/analytics/constants'
import { v4 as uuidv4 } from 'uuid'

import { searchModuleAnalytics } from '../../../../lib/@getethos/analytics/analyticsEvents'
import styles from '../SearchIndex.module.scss'
import { LENGTHS, VARIANTS } from '../constants'

/**
 * FullResult is a longer result snippet with a caption and description
 * We use this in what's called the 'Full' variation of the SearchIndex module
 *
 * @param {string} url - The URL for the result
 * @param {string} title - The title for the result
 * @param {string} caption - The caption for the result
 * @param {string} description - The description for the result
 * @param {string} query - The search query being searched
 * @param {number} rank - The rank (order) of the result amongst sibling results
 *
 * @return {JSX.Element} A link to the search result wrapping the title of the result
 */

interface FullResultProps {
  url: string
  title: string
  caption?: string
  description: string
  query: string
  rank: number
  useNewFonts?: boolean
}

export const FullResult = ({
  url,
  title,
  caption,
  description,
  query,
  rank,
  useNewFonts,
}: FullResultProps): JSX.Element => {
  const trackFullResultClick = (eventId?: string): void => {
    searchModuleAnalytics.resultClicked({
      properties: {
        moduleVariation: VARIANTS.FULL,
        searchTerm: query,
        resultRank: rank,
        resultUrl: url,
      },
      sendServiceEvent: false,
      sendSegmentEvent: true,
      eventId,
    })
  }

  const preparedDescription =
    description.length > LENGTHS.MAX_DESCRIPTION
      ? `${description.substring(0, LENGTHS.MAX_DESCRIPTION)}...`
      : description

  const eventId = uuidv4()

  return (
    <>
      <div className={`${useNewFonts ? 'new-title-s' : 'old-title-small-500'}`}>
        <Link
          href={url}
          onClick={() => trackFullResultClick(eventId)}
          data-properties={JSON.stringify({
            key: ANALYTICS_SERVICE_KEYS.SEARCH_RESULT,
            label: ANALYTICS_SERVICE_LABELS.LINK,
            region: MODULES.SEARCH_INDEX,
            search: {
              searchTerm: query,
              resultRank: rank,
              resultUrl: url,
            },
            target: url,
            eventId,
          })}
        >
          {title}
        </Link>
      </div>
      {caption && (
        <div className={styles.caption}>
          <Spacer.H4 />
          <div
            className={`${
              useNewFonts ? 'new-text-s-regular' : 'old-caption-regular-400'
            }`}
          >
            {caption}
          </div>
        </div>
      )}
      <Spacer.H8 />
      {preparedDescription && (
        <div
          className={classNames(
            'text-[var(--GraySecondary--opaque)]',
            useNewFonts ? 'new-text-s-regular' : 'old-footnote-regular-400'
          )}
        >
          {preparedDescription}
        </div>
      )}
      <Spacer.H24 />
    </>
  )
}
